import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X, Zap, Lock, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTrialStatus } from '@/context/TrialContext';

/**
 * TrialExpiredBanner - Banner peringatan yang muncul di semua halaman
 * ketika trial user sudah berakhir.
 * 
 * Mode:
 * - "Read-Only + AI Mati": User masih bisa melihat data, tapi tidak bisa
 *   melakukan aksi seperti: buat order, kirim pesan, edit produk, dll.
 */
const TrialExpiredBanner = () => {
  const navigate = useNavigate();
  const { isTrialExpired, isTrialUser } = useTrialStatus();
  const [dismissed, setDismissed] = useState(false);

  // Hanya tampilkan jika trial user dan trial sudah expired
  if (!isTrialUser || !isTrialExpired || dismissed) {
    return null;
  }

  return (
    <div className="sticky top-0 z-20 w-full lg:pl-64" data-testid="trial-expired-banner">
      {/* Main Banner */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            {/* Left: Icon + Message */}
            <div className="flex items-center gap-2.5 sm:gap-3 flex-1 min-w-0">
              <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/20 flex items-center justify-center dark:bg-black/25">
                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-sm sm:text-base leading-tight">
                  Masa Trial Anda Telah Berakhir
                </h3>
                <p className="text-red-100 text-xs sm:text-sm mt-0.5">
                  <span className="sm:hidden">Mode Read-Only, AI Chatbot mati.</span>
                  <span className="hidden sm:inline">
                    Akun dalam mode <span className="font-semibold">Read-Only</span>. 
                    AI Chatbot dan fitur lainnya dinonaktifkan. Upgrade sekarang untuk melanjutkan.
                  </span>
                </p>
              </div>
            </div>

            {/* Right: CTA + Dismiss */}
            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              <Button
                onClick={() => navigate('/payment?plan=starter')}
                className="bg-white text-red-600 hover:bg-red-50 font-semibold text-xs sm:text-sm px-2.5 sm:px-4 h-8 sm:h-9"
                data-testid="trial-expired-upgrade-btn"
              >
                <Zap className="w-3.5 h-3.5 mr-1" />
                Upgrade
              </Button>
              <button
                onClick={() => setDismissed(true)}
                className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Tutup"
                data-testid="trial-expired-dismiss-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Info Bar — di HP diringkas jadi satu baris supaya banner tidak setinggi layar */}
      <div className="bg-red-800/90 text-red-100 text-[11px] sm:text-xs py-1.5 px-3 sm:px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 sm:gap-4 flex-wrap">
          <div className="flex items-center gap-1.5 sm:hidden">
            <Lock className="w-3 h-3 flex-shrink-0" />
            <span>Order, Kirim Pesan &amp; AI Chatbot: Nonaktif</span>
          </div>
          <div className="hidden sm:flex items-center gap-1.5">
            <Lock className="w-3 h-3" />
            <span>Buat Order: Nonaktif</span>
          </div>
          <span className="hidden sm:inline text-red-400">•</span>
          <div className="hidden sm:flex items-center gap-1.5">
            <Lock className="w-3 h-3" />
            <span>Kirim Pesan: Nonaktif</span>
          </div>
          <span className="hidden sm:inline text-red-400">•</span>
          <div className="hidden sm:flex items-center gap-1.5">
            <Lock className="w-3 h-3" />
            <span>AI Chatbot: Nonaktif</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * TrialExpiredOverlay - Overlay yang muncul di atas tombol aksi
 * untuk menunjukkan bahwa aksi tidak bisa dilakukan.
 */
export const TrialExpiredOverlay = ({ children, actionName = 'Aksi ini' }) => {
  const navigate = useNavigate();
  const { isTrialExpired } = useTrialStatus();

  if (!isTrialExpired) {
    return children;
  }

  return (
    <div className="relative">
      {/* Original content with reduced opacity */}
      <div className="opacity-50 pointer-events-none select-none">
        {children}
      </div>
      
      {/* Overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-slate-900/10 rounded-lg backdrop-blur-[1px]">
        <div className="text-center p-3">
          <Lock className="w-5 h-5 text-slate-500 mx-auto mb-1 dark:text-zinc-400" />
          <p className="text-xs text-slate-600 font-medium mb-2">
            {actionName} dinonaktifkan
          </p>
          <Button
            size="sm"
            onClick={() => navigate('/payment?plan=starter')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-7 px-3"
          >
            <Zap className="w-3 h-3 mr-1" />
            Upgrade
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * DisabledButton - Button wrapper yang disable dan tampilkan tooltip
 * ketika trial expired.
 */
export const TrialGatedButton = ({ 
  children, 
  onClick, 
  disabled, 
  className,
  tooltipText = 'Upgrade untuk menggunakan fitur ini',
  ...props 
}) => {
  const navigate = useNavigate();
  const { isTrialExpired } = useTrialStatus();

  if (isTrialExpired) {
    return (
      <div className="relative group">
        <Button
          {...props}
          className={`${className} opacity-50 cursor-not-allowed`}
          disabled={true}
        >
          <Lock className="w-3.5 h-3.5 mr-1" />
          {children}
        </Button>
        
        {/* Tooltip */}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
          <div className="flex items-center gap-2">
            <span>{tooltipText}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate('/payment?plan=starter');
              }}
              className="text-blue-400 hover:text-blue-300 font-medium underline"
            >
              Upgrade
            </button>
          </div>
          {/* Arrow */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
        </div>
      </div>
    );
  }

  return (
    <Button onClick={onClick} disabled={disabled} className={className} {...props}>
      {children}
    </Button>
  );
};

/**
 * Small inline warning for expired trial
 */
export const TrialExpiredInlineWarning = ({ compact = false }) => {
  const navigate = useNavigate();
  const { isTrialExpired } = useTrialStatus();

  if (!isTrialExpired) {
    return null;
  }

  if (compact) {
    return (
      <span className="inline-flex items-center gap-1 text-red-600 text-xs dark:text-red-400">
        <Lock className="w-3 h-3" />
        <span>Read-Only</span>
      </span>
    );
  }

  return (
    <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
      <span>Trial berakhir - Mode Read-Only</span>
      <button
        onClick={() => navigate('/payment?plan=starter')}
        className="ml-auto text-red-600 hover:text-red-700 font-medium underline text-xs dark:text-red-400"
      >
        Upgrade
      </button>
    </div>
  );
};

export default TrialExpiredBanner;
